// LISTA-EXPORT HOOK — a hat érintett lista-oldal (ügyfelek · fiókok · kapcsolattartók · munkatársak · eszközök ·
// eseménynapló) EGYETLEN export-vezérlője: sorok begyűjtése lapozással → szerializálás → letöltés → visszajelzés.
//
// MIÉRT HOOK ÉS NEM PER-OLDAL KÓD: a művelet hat helyen ugyanaz, és három olyan részlete van, amit oldalonként
// külön megírva biztosan elcsúsztatnánk — (1) a lapozás plafonjai és a CSONKOLÁS JELZÉSE, (2) a `pending`
// életciklus (a gomb tiltása a több-kérésű gyűjtés alatt), (3) a toast-taxonómia (siker darabszámmal / részleges
// / hiba). Az oldal így csak azt adja meg, AMI valóban oldal-specifikus: a lap-lekérő és a fájlnév-slug.
//
// A SOR-KÉSZLET a lista-VÉGPONTRÓL jön, az ÉPPEN AKTÍV szűrőkkel — nem a betöltött lapokból és nem a DOM-ból
// (a legacy DOM-kaparása max 50 sort exportált; lásd `lib/export/index.ts`).
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { DataTableColumn } from '@/components/shared/data-table';
import { api } from '@/lib/api';
import { collectPages, exportColumnsOf, exportRows, type ExportFormat } from '@/lib/export';

export interface UseListExportInput<T, C> {
  /** A tábla-oszlopok — az export az `exportValue`-val ellátottakat viszi, a tábla sorrendjében. */
  columns: DataTableColumn<T>[];
  /** Fájlnév-slug (ANGOL, nyelv-független) — pl. `contacts`. */
  slug: string;
  /**
   * Egy lap az AKTÍV szűrőkkel. `undefined` cursor = első lap. Visszatérés `null` → a lap nem tölthető
   * (jog/hálózat) → a hook a már összegyűjtött sorokat exportálja, RÉSZLEGES figyelmeztetéssel.
   */
  fetchPage: (cursor: C | undefined) => Promise<{ rows: T[]; next: C | null } | null>;
  /**
   * Az AKTÍV szűrők olvasható pillanatképe az AUDIT-nyomhoz (`ki mit, MILYEN SZŰRŐVEL, hány sort`). Csak a
   * BFF whitelistjén szereplő kulcsok kerülnek a naplóba; az üres/`undefined` értékek kimaradnak. Elhagyható
   * (akkor a nyom a modult, a formátumot és a darabszámot rögzíti).
   */
  filters?: Record<string, string | number | boolean | undefined | null>;
}

export interface UseListExportResult {
  /** Az export indítása a választott formátumban (a `ListExportButton` hívja). */
  run: (format: ExportFormat) => void;
  /** Fut-e éppen (gyűjtés vagy írás) — a gomb ilyenkor tiltott. */
  pending: boolean;
}

export function useListExport<T, C>({
  columns,
  slug,
  fetchPage,
  filters,
}: UseListExportInput<T, C>): UseListExportResult {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);

  const run = useCallback(
    (format: ExportFormat) => {
      setPending(true);
      void (async () => {
        try {
          const { rows, truncated } = await collectPages<T, C>(fetchPage);
          if (rows.length === 0) {
            toast.error(t('common.export.empty'));
            return;
          }
          await exportRows({ format, columns: exportColumnsOf(columns), rows, slug });
          // AUDIT-NYOM a SIKERES letöltés után (a fájl ekkor már a felhasználónál van). Az export a felület
          // legexfil-relevánsabb művelete — PII-tömeg hagyja el a rendszert —, és a platform naplójában a
          // lapozás csak „olvasás"-ként látszik: ez a hívás rögzíti, hogy FÁJL is készült belőle.
          // SZÁNDÉKOSAN fire-and-forget: az audit-nyom hibája NEM buktathatja a már megtörtént exportot, és
          // nem is késleltetheti a visszajelzést (a hiba a szerver-logban marad).
          void recordExportAudit({
            module: slug,
            format,
            rowCount: rows.length,
            truncated,
            ...(filters ? { filters } : {}),
          });
          // A CSONKOLÁS SOSE csendes: a „kész" toast azt ígérné, hogy minden benne van.
          if (truncated) toast.warning(t('common.export.partial', { count: rows.length }));
          else toast.success(t('common.export.done', { count: rows.length }));
        } catch {
          // Nyers hiba SOHA nem megy a felhasználóhoz (lehet upstream-üzenet vagy stack).
          toast.error(t('common.export.failed'));
        } finally {
          setPending(false);
        }
      })();
    },
    [columns, fetchPage, filters, slug, t],
  );

  return { run, pending };
}

/**
 * Az audit-nyom elküldése a BFF-nek. Sosem dob: a hívó (`run`) `void`-dal indítja, mert az export MÁR
 * megtörtént — egy hálózati hiba itt nem a felhasználó problémája. A szűrő-értékek stringgé alakítva mennek
 * (a napló szöveges pillanatképet tart), az üres/`undefined` kulcsok kimaradnak.
 */
async function recordExportAudit(input: {
  module: string;
  format: ExportFormat;
  rowCount: number;
  truncated: boolean;
  filters?: Record<string, string | number | boolean | undefined | null>;
}): Promise<void> {
  const filters: Record<string, string> = {};
  for (const [k, v] of Object.entries(input.filters ?? {})) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    filters[k] = String(v);
  }
  try {
    await api.exportAudit.mutation({
      body: {
        module: input.module,
        format: input.format,
        rowCount: input.rowCount,
        truncated: input.truncated,
        ...(Object.keys(filters).length > 0 ? { filters } : {}),
      },
    });
  } catch {
    // Csendes: az audit-nyom hibája nem a felhasználó dolga (és nem is buktathatja az exportot).
  }
}
